import { useId, useState } from 'react';
import DOMPurify from 'dompurify';
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { Checkbox } from '@/components/atoms/checkbox';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { RichTextEditor } from '@/components/molecules/rich-text-editor';
import { useCreateTermsTemplate, useTermsTemplates } from '@/hooks/settings/useTermsTemplates';
import { RICH_TEXT_PREVIEW_CLASS } from '@/lib/rich-text';
import { findDefaultTermsTemplate, joinTermsContent, type TermsSelection } from '@/lib/terms';
import type { TermsTemplate, TermsType } from '@/hooks/settings/useTermsTemplates';
import { cn } from '@/lib/utils';

const CUSTOM_VALUE = '__custom__';
const DEFAULT_VALUE = '__default__';
const ADD_PLACEHOLDER = '__add__';

interface TermsSelectorProps {
  value: TermsSelection;
  onChange: (next: TermsSelection) => void;
  /**
   * What "Default" means here. Documents inherit through a parent (a job type
   * or estimate template) before reaching the tenant default, so the label is
   * caller-supplied — e.g. "Inherited from job type".
   */
  defaultHint?: string;
  disabled?: boolean;
  customPlaceholder?: string;
  /**
   * True while the caller is still working out what this document inherits.
   *
   * Without it the selector renders "No terms will be shown on this document."
   * the moment it mounts, which is a claim it cannot support yet: a create form
   * seeds from the source job or the tenant default once those queries land, and
   * the server applies that same inheritance if the form is submitted first. The
   * operator would be told there are no terms and then get some.
   */
  resolving?: boolean;
  /**
   * Which terms to offer and to add to: sales terms on estimates, jobs and the
   * rest; purchase terms on purchase orders.
   */
  type?: TermsType;
}

/**
 * Pick Terms & Conditions: the tenant default, one or MORE repository entries
 * stacked in order, or one-off custom text. Used on estimates, jobs, job types
 * and estimate templates so the same choice reads identically everywhere.
 *
 * Stacking is why the template branch is a list, not a radio: a document's
 * terms are usually several separate agreements read in a fixed order —
 * general conditions, then a warranty, then a financing disclosure — and the
 * customer sees them piled one after the other, in exactly this order.
 */
export function TermsSelector({
  value,
  onChange,
  defaultHint,
  disabled = false,
  resolving = false,
  customPlaceholder = 'Terms and conditions...',
  type = 'SALES',
}: TermsSelectorProps) {
  const { data: templates = [], isLoading, isSuccess } = useTermsTemplates(type);
  const defaultTemplate = findDefaultTermsTemplate(templates);

  // Quick-create: promote the custom text being typed here into a named
  // repository entry, without leaving the estimate/job/job type being edited.
  const createTemplate = useCreateTermsTemplate();
  // Per-instance id: this component is shared across four forms, and a
  // hard-coded one would break label/input association the moment two ever
  // render in the same tree.
  const saveToRepositoryId = useId();
  const [saveToRepository, setSaveToRepository] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const customBody = value.termsAndConditions.trim();
  const canSaveToRepository =
    Boolean(newTemplateName.trim()) && Boolean(customBody) && !createTemplate.isPending;

  function resetQuickCreate() {
    setSaveToRepository(false);
    setNewTemplateName('');
  }

  function handleSaveToRepository() {
    if (!canSaveToRepository) return;
    createTemplate.mutate(
      { name: newTemplateName.trim(), content: value.termsAndConditions, type },
      {
        onSuccess: (created) => {
          resetQuickCreate();
          // The record KEEPS its own custom wording and stays CUSTOM — saving to
          // the repository files a reusable copy for later, it does not re-point
          // this document at it. Switching it to TEMPLATE would silently make
          // the two follow each other from here on, which is a different (and
          // unasked-for) decision than "also keep this for next time".
          toast.success(`Saved "${created.name}" to the repository.`);
        },
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Failed to save the template'),
      },
    );
  }

  const stack = value.termsMode === 'TEMPLATE' ? value.termsTemplateIds : [];
  const stackEntries = stack.map((id) => ({
    id,
    template: templates.find((t) => t.id === id) ?? null,
  }));

  /** Re-point the selection at a new stack, re-deriving the joined snapshot. */
  function applyStack(nextIds: string[]) {
    // Entries deleted from the repository while this form was open drop out on
    // the user's next touch. Gated on a SUCCESSFUL load: a failed fetch also
    // reads as "no templates", and filtering against that would strip every
    // valid pin. (The server applies the same drop-the-dead rule on save, so a
    // stale id that slips through never fails the save either.)
    const ids = isSuccess ? nextIds.filter((id) => templates.some((t) => t.id === id)) : nextIds;
    if (ids.length === 0) {
      // Emptying the stack is choosing the default, not choosing "no terms".
      onChange({
        termsMode: 'DEFAULT',
        termsTemplateIds: defaultTemplate ? [defaultTemplate.id] : [],
        termsAndConditions: defaultTemplate?.content ?? '',
      });
      return;
    }
    onChange({
      termsMode: 'TEMPLATE',
      termsTemplateIds: ids,
      termsAndConditions: joinTermsContent(
        ids.map((id) => templates.find((t) => t.id === id)?.content),
      ),
    });
  }

  const selectValue =
    value.termsMode === 'CUSTOM'
      ? CUSTOM_VALUE
      : value.termsMode === 'TEMPLATE' && stack.length > 0
        ? (stack[0] ?? DEFAULT_VALUE)
        : DEFAULT_VALUE;

  // The trigger describes the WHOLE stack; Radix would only name the first.
  const triggerLabel =
    value.termsMode === 'CUSTOM'
      ? 'Custom…'
      : value.termsMode === 'TEMPLATE' && stack.length > 0
        ? stackEntries.map((e) => e.template?.name ?? '(removed)').join(' + ')
        : (defaultHint ??
          (defaultTemplate ? `Default — ${defaultTemplate.name}` : 'Default (none set)'));

  // A pinned entry that has since been deleted: without this the row renders
  // "(removed)" with no explanation of what happened. Only a successful load
  // proves deletion — a failed fetch says nothing about the repository.
  const pinnedMissing =
    value.termsMode === 'TEMPLATE' &&
    isSuccess &&
    stack.some((id) => !templates.some((t) => t.id === id));

  function handleSelect(next: string) {
    if (next !== CUSTOM_VALUE) resetQuickCreate();
    if (next === CUSTOM_VALUE) {
      // Seed the editor with whatever is on screen so switching to Custom is a
      // starting point to edit, not a blank page.
      onChange({
        termsMode: 'CUSTOM',
        termsTemplateIds: [],
        termsAndConditions: value.termsAndConditions,
      });
      return;
    }
    if (next === DEFAULT_VALUE) {
      onChange({
        termsMode: 'DEFAULT',
        termsTemplateIds: defaultTemplate ? [defaultTemplate.id] : [],
        termsAndConditions: defaultTemplate?.content ?? '',
      });
      return;
    }
    // Picking a template starts a fresh stack; the "Add" control below is how
    // more entries pile on. Replacing (rather than appending) here keeps the
    // main dropdown's long-standing meaning: "these are the terms now".
    applyStack([next]);
  }

  function handleAdd(next: string) {
    if (next === ADD_PLACEHOLDER || stack.includes(next)) return;
    applyStack([...stack, next]);
  }

  function moveEntry(index: number, delta: -1 | 1) {
    const target = index + delta;
    if (target < 0 || target >= stack.length) return;
    const ids = [...stack];
    const [moved] = ids.splice(index, 1);
    ids.splice(target, 0, moved!);
    applyStack(ids);
  }

  function removeEntry(index: number) {
    applyStack(stack.filter((_, i) => i !== index));
  }

  function createAndAddTemplate(
    draft: { name: string; content: string },
    afterSuccess: () => void,
  ) {
    createTemplate.mutate(
      { name: draft.name.trim(), content: draft.content, type },
      {
        onSuccess: (created) => {
          // The newly-created entry is not in the query cache yet. Build the
          // selection from the response instead of passing through `applyStack`,
          // whose stale-repository guard would correctly (but unhelpfully here)
          // filter that brand-new id until the invalidated query refetches.
          onChange({
            termsMode: 'TEMPLATE',
            termsTemplateIds: [...stack, created.id],
            termsAndConditions: joinTermsContent([
              ...stackEntries.map((entry) => entry.template?.content),
              created.content,
            ]),
          });
          afterSuccess();
          toast.success(`Created and added "${created.name}".`);
        },
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Failed to create the template'),
      },
    );
  }

  const addable = templates.filter((t) => !stack.includes(t.id));
  const preview = value.termsAndConditions.trim();

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label className="text-xs">Terms &amp; Conditions</Label>
        <Select value={selectValue} onValueChange={handleSelect} disabled={disabled || isLoading}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select terms">{triggerLabel}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={DEFAULT_VALUE}>
              {defaultHint ??
                (defaultTemplate ? `Default — ${defaultTemplate.name}` : 'Default (none set)')}
            </SelectItem>
            {templates.length > 0 ? <SelectSeparator /> : null}
            {templates.map((template) => (
              <SelectItem key={template.id} value={template.id}>
                {template.name}
              </SelectItem>
            ))}
            <SelectSeparator />
            <SelectItem value={CUSTOM_VALUE}>Custom…</SelectItem>
          </SelectContent>
        </Select>
        {pinnedMissing ? (
          <p className="text-xs text-destructive">
            A selected template was deleted from the repository, so it will be left out of this
            document&apos;s terms. Remove it from the stack, or switch to Custom to keep its old
            wording.
          </p>
        ) : null}
        {templates.length === 0 && isSuccess ? (
          <p className="text-xs text-muted-foreground">
            No templates yet — write custom terms below and tick{' '}
            <span className="font-medium">Save to repository</span> to reuse them, or{' '}
            <Link
              to="/dashboard/settings/terms-and-conditions"
              search={{ type }}
              className="underline underline-offset-2"
            >
              manage them in settings
            </Link>
            .
          </p>
        ) : null}
      </div>

      {value.termsMode !== 'CUSTOM' && !(value.termsMode === 'TEMPLATE' && stack.length > 0) ? (
        // Nothing stacked yet: a new entry can still be written here, filed in
        // the repository and put on this document in one go.
        <CreateTermsInline
          disabled={disabled}
          creating={createTemplate.isPending}
          onCreate={createAndAddTemplate}
        />
      ) : null}

      {value.termsMode === 'TEMPLATE' && stack.length > 0 ? (
        <TermsStack
          entries={stackEntries}
          disabled={disabled}
          addable={addable}
          creating={createTemplate.isPending}
          onAdd={handleAdd}
          onCreateAndAdd={createAndAddTemplate}
          onMove={moveEntry}
          onRemove={removeEntry}
        />
      ) : null}

      {value.termsMode === 'CUSTOM' ? (
        <div className="space-y-3">
          <RichTextEditor
            value={value.termsAndConditions}
            onChange={(termsAndConditions) => onChange({ ...value, termsAndConditions })}
            placeholder={customPlaceholder}
          />
          <div className="space-y-2 rounded-md border bg-muted/20 px-3 py-2.5">
            <div className="flex items-center gap-2">
              <Checkbox
                id={saveToRepositoryId}
                checked={saveToRepository}
                disabled={disabled}
                onCheckedChange={(checked) => {
                  const next = checked === true;
                  setSaveToRepository(next);
                  if (!next) setNewTemplateName('');
                }}
              />
              <Label htmlFor={saveToRepositoryId} className="cursor-pointer text-xs font-normal">
                Save to repository
              </Label>
            </div>
            {saveToRepository ? (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Input
                    value={newTemplateName}
                    onChange={(event) => setNewTemplateName(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        // The selector is rendered inside host forms; Enter here
                        // must save the template, not submit the estimate.
                        event.preventDefault();
                        handleSaveToRepository();
                      }
                    }}
                    placeholder="Name it, e.g. Commercial Install Terms"
                    className="h-8 text-sm"
                    disabled={disabled || createTemplate.isPending}
                    maxLength={200}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="h-8 shrink-0"
                    disabled={disabled || !canSaveToRepository}
                    onClick={handleSaveToRepository}
                  >
                    {createTemplate.isPending ? 'Saving...' : 'Save'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {customBody
                    ? 'Files a reusable copy. This record keeps its own wording.'
                    : 'Write the terms above first.'}
                </p>
              </div>
            ) : null}
          </div>
        </div>
      ) : preview ? (
        // Read-only rendering of the stacked wording, exactly as the customer
        // will read it. Editing here would drift from the repository entries
        // without saying so; "Custom…" is the way to change the words for one
        // record.
        <div
          className={cn(
            RICH_TEXT_PREVIEW_CLASS,
            'max-h-48 overflow-y-auto rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground',
          )}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value.termsAndConditions) }}
        />
      ) : resolving ? (
        <p className="text-xs text-muted-foreground">Working out the terms for this document…</p>
      ) : (
        <p className="text-xs text-muted-foreground">No terms will be shown on this document.</p>
      )}
    </div>
  );
}

/**
 * The ordered stack: one row per pinned entry with reorder/remove, and a picker
 * to pile on another. Order is the contract — the document renders these top
 * to bottom exactly as listed here.
 */
function TermsStack({
  entries,
  addable,
  creating,
  disabled,
  onAdd,
  onCreateAndAdd,
  onMove,
  onRemove,
}: {
  entries: { id: string; template: TermsTemplate | null }[];
  addable: TermsTemplate[];
  creating: boolean;
  disabled: boolean;
  onAdd: (id: string) => void;
  onCreateAndAdd: (draft: { name: string; content: string }, afterSuccess: () => void) => void;
  onMove: (index: number, delta: -1 | 1) => void;
  onRemove: (index: number) => void;
}) {
  return (
    <div className="space-y-1.5 rounded-md border bg-muted/20 px-2.5 py-2">
      {entries.map((entry, index) => (
        <div key={entry.id} className="flex items-center gap-1.5">
          <span className="w-4 shrink-0 text-right text-[10px] tabular-nums text-muted-foreground">
            {index + 1}.
          </span>
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-xs',
              entry.template ? '' : 'text-destructive',
            )}
          >
            {entry.template?.name ?? '(removed)'}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-6"
            aria-label={`Move up: ${entry.template?.name ?? 'removed template'}`}
            disabled={disabled || index === 0}
            onClick={() => onMove(index, -1)}
          >
            <ArrowUp className="size-3" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-6"
            aria-label={`Move down: ${entry.template?.name ?? 'removed template'}`}
            disabled={disabled || index === entries.length - 1}
            onClick={() => onMove(index, 1)}
          >
            <ArrowDown className="size-3" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-6"
            aria-label={`Remove: ${entry.template?.name ?? 'removed template'}`}
            disabled={disabled}
            onClick={() => onRemove(index)}
          >
            <X className="size-3" />
          </Button>
        </div>
      ))}
      <div className="space-y-2 border-t pt-2">
        {addable.length > 0 ? (
          <Select value={ADD_PLACEHOLDER} onValueChange={onAdd} disabled={disabled}>
            <SelectTrigger className="h-7 w-full border-dashed text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Plus className="size-3" /> Add another Terms &amp; Conditions
              </span>
            </SelectTrigger>
            {/* position="popper" is load-bearing: this trigger renders a fixed
              label instead of a SelectValue, and the default item-aligned mode
              positions itself relative to the SelectValue node — without one it
              silently never places (or focuses) the open list, so the picker
              reads as a dead control. Popper anchors to the trigger instead. */}
            <SelectContent position="popper">
              {addable.map((template) => (
                <SelectItem key={template.id} value={template.id}>
                  {template.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <p className="text-xs text-muted-foreground">
            All repository terms are already selected. Create another entry below to add more.
          </p>
        )}

        <CreateTermsInline disabled={disabled} creating={creating} onCreate={onCreateAndAdd} />
      </div>
    </div>
  );
}

/**
 * Write a new entry, file it in the repository and add it to this document:
 * under the stack, and on its own while nothing is stacked yet.
 */
function CreateTermsInline({
  disabled,
  creating,
  onCreate,
}: {
  disabled: boolean;
  creating: boolean;
  onCreate: (draft: { name: string; content: string }, afterSuccess: () => void) => void;
}) {
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newContent, setNewContent] = useState('');
  const canCreate = Boolean(newName.trim()) && Boolean(newContent.trim()) && !creating;

  function resetCreate() {
    setShowCreate(false);
    setNewName('');
    setNewContent('');
  }

  function handleCreate() {
    if (!canCreate) return;
    onCreate({ name: newName, content: newContent }, resetCreate);
  }

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 w-full justify-start border border-dashed text-xs font-normal text-muted-foreground"
        aria-expanded={showCreate}
        disabled={disabled}
        onClick={() => setShowCreate((open) => !open)}
      >
        <Plus className="mr-1 size-3" />
        Create new Terms &amp; Conditions and add it
      </Button>

      {showCreate ? (
        <div className="space-y-2 rounded-md border bg-background p-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Name</Label>
            <Input
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.preventDefault();
              }}
              placeholder="e.g. Warranty terms"
              className="h-8 text-sm"
              disabled={disabled || creating}
              maxLength={200}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Content</Label>
            <RichTextEditor
              value={newContent}
              onChange={setNewContent}
              placeholder="Enter the additional terms and conditions..."
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={creating}
              onClick={resetCreate}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={disabled || !canCreate}
              onClick={handleCreate}
            >
              {creating ? 'Creating...' : 'Create and add'}
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}
